import React from 'react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import type { EventInfo } from '@ckeditor/ckeditor5-utils';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import type { EditorConfig } from '@ckeditor/ckeditor5-core';
import classnames from 'classnames';
import styles from './richtextEditor.module.scss';

const DEFAULT_TOOLBAR = ['bold', 'italic', 'numberedList', 'bulletedList'];

interface RichTextEditorProps {
  value: string;
  readOnly?: boolean;
  onChange: (value: string) => void;
  toolbar?: string[];
  autoFocus?: boolean;
}

const RichTextEditor = ({
  value,
  readOnly,
  onChange,
  toolbar,
  autoFocus,
}: RichTextEditorProps) => {
  const config: EditorConfig = {
    toolbar: toolbar || DEFAULT_TOOLBAR,
    removePlugins: [
      // disabled autoformat to avoid inline markdown
      'Autoformat',
    ],
    link: {
      defaultProtocol: 'https',
      // Automatically add target="_blank" and rel="noopener noreferrer" to all external links
      addTargetToExternalLinks: true,
    },
  };

  const handleOnChange = (_: EventInfo, editor: ClassicEditor) => {
    const data = editor.getData();
    onChange(data);
  };

  return (
    <div
      className={classnames(styles.root, {
        [styles.disabled]: readOnly,
      })}
    >
      <CKEditor
        config={config}
        editor={ClassicEditor}
        data={value}
        onChange={handleOnChange}
        disabled={readOnly}
        onReady={(editor) => {
          if (autoFocus) {
            editor.model.change((writer) => {
              const root = editor.model.document.getRoot();
              if (root) {
                writer.setSelection(writer.createPositionAt(root, 'end'));
              }
            });
            editor.focus();
          }
        }}
        data-cy="contact-details-description-modal:rich-text-editor-input"
      />
    </div>
  );
};

export default RichTextEditor;
